import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { useCart } from "../context/CartContext";
import { money } from "../config/brand";

export const CartDrawer = () => {
  const { items, isOpen, closeCart, setQty, remove, subtotal } = useCart();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            data-testid="cart-backdrop"
            className="fixed inset-0 z-50 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeCart}
          />
          <motion.aside
            data-testid="cart-drawer"
            className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-50 flex flex-col"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.76, 0, 0.24, 1] }}
          >
            <div className="flex items-center justify-between h-14 px-5 border-b border-black/10 flex-shrink-0">
              <p className="text-[11px] font-medium tracking-[0.18em]">
                CART ({items.reduce((s, i) => s + i.qty, 0)})
              </p>
              <button data-testid="cart-close-button" onClick={closeCart} aria-label="Close cart">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-5">
              {items.length === 0 && (
                <p className="pt-10 text-[11px] tracking-[0.18em] text-black/50" data-testid="cart-empty">
                  YOUR CART IS EMPTY
                </p>
              )}
              {items.map((i) => (
                <div key={i.key} className="flex gap-4 py-5 border-b border-black/10" data-testid={`cart-item-${i.slug}`}>
                  <Link to={`/products/${i.slug}`} onClick={closeCart} className="w-20 flex-shrink-0">
                    <div className="aspect-[1/1] bg-white overflow-hidden">
                      {i.image && (
                        <img src={i.image} alt={i.name} className="w-full h-full object-contain" loading="lazy" />
                      )}
                    </div>
                  </Link>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <Link
                        to={`/products/${i.slug}`}
                        onClick={closeCart}
                        className="text-[11px] font-medium tracking-[0.08em] uppercase leading-snug"
                      >
                        {i.name}
                      </Link>
                      <button
                        data-testid={`cart-remove-${i.slug}`}
                        onClick={() => remove(i.key)}
                        aria-label={`Remove ${i.name}`}
                        className="text-black/40 hover:text-black transition-colors"
                      >
                        <X className="w-4 h-4" strokeWidth={1.5} />
                      </button>
                    </div>
                    {i.size && (
                      <p className="text-[10px] text-black/50 mt-1 tracking-[0.1em]">SIZE {i.size}</p>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center border border-black/20">
                        <button
                          data-testid={`cart-qty-minus-${i.slug}`}
                          onClick={() => setQty(i.key, i.qty - 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="w-8 text-center text-[11px]" data-testid={`cart-qty-${i.slug}`}>
                          {i.qty}
                        </span>
                        <button
                          data-testid={`cart-qty-plus-${i.slug}`}
                          onClick={() => setQty(i.key, i.qty + 1)}
                          className="w-7 h-7 flex items-center justify-center hover:bg-black hover:text-white transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <p className="text-[11px]">{money(i.price * i.qty)}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {items.length > 0 && (
              <div className="border-t border-black/10 px-5 py-5 flex-shrink-0">
                <div className="flex justify-between text-[11px] tracking-[0.12em] mb-4">
                  <span>SUBTOTAL</span>
                  <span data-testid="cart-subtotal">{money(subtotal)}</span>
                </div>
                <Link
                  to="/checkout"
                  onClick={closeCart}
                  data-testid="cart-checkout-button"
                  className="block w-full bg-black text-white text-center text-[11px] font-medium tracking-[0.2em] py-4 hover:bg-[#e50000] transition-colors"
                >
                  CHECKOUT
                </Link>
                <Link
                  to="/cart"
                  onClick={closeCart}
                  data-testid="cart-view-link"
                  className="block text-center text-[10px] tracking-[0.18em] mt-3 underline underline-offset-4 hover:opacity-50 transition-opacity"
                >
                  VIEW CART
                </Link>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
